import React, { useState, useEffect, useMemo } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CategoryTreeNode from './components/CategoryTreeNode';
import ServiceEditPanel from './components/ServiceEditPanel';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import ValidationTestPanel from './components/ValidationTestPanel';
import SearchFilterBar from './components/SearchFilterBar';
import ServiceZoneManager from './components/ServiceZoneManager';
import { categoryService, equipmentService, problemService, serviceService } from '../../services/supabaseService';
import { bunny } from '../../lib/bunnyNet';

const ServiceCatalogManager = () => {
  const [catalogData, setCatalogData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [parentNode, setParentNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [showValidationPanel, setShowValidationPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchCatalogData();
  }, []);

  const fetchCatalogData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await categoryService.getCategories();
      if (fetchError) {
        console.error('Error fetching categories:', fetchError);
        setError('Erreur lors du chargement du catalogue. Veuillez réessayer.');
        return;
      }

      const transformedData = transformSupabaseData(data || []);
      setCatalogData(transformedData);
      
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Une erreur inattendue est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const transformSupabaseData = (categories) => {
    // Trier les catégories par leur ordre
    const sortedCategories = [...categories].sort((a, b) => {
      const orderA = a.order || 0;
      const orderB = b.order || 0;
      return orderA - orderB;
    });

    return sortedCategories.map(category => {
      const extractImageUrl = (imageArray) => {
        if (!imageArray || imageArray.length === 0) return null;
        const imageUrl = imageArray[0];
        if (imageUrl && imageUrl.startsWith('http')) {
          return imageUrl;
        }
        return imageUrl ? `https://${process.env.REACT_APP_BUNNY_STORAGE_ZONE}.b-cdn.net/${imageUrl}` : null;
      };

      // Trier les équipements par leur ordre
      const sortedEquipment = (category.equipment || []).sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });

      return {
        id: category.id,
        name: category.name || 'Sans nom',
        type: 'category',
        active: category.actif !== false,
        description: category.description,
        imageUrl: extractImageUrl(category.Image),
        logoUrl: category.logo,
        order: category.order || 0,
        createdAt: category.created_at,
        updatedAt: category.updated_at,
        children: sortedEquipment.map(equipment => {
          // Trier les problèmes par leur ordre
          const sortedProblems = (equipment.problem || []).sort((a, b) => {
            const orderA = a.order || 0;
            const orderB = b.order || 0;
            return orderA - orderB;
          });

          return {
            id: equipment.id,
            name: equipment.name || 'Équipement sans nom',
            type: 'equipment',
            active: equipment.actif !== false,
            categoryId: equipment.category,
            category_id: equipment.category,
            description: equipment.description,
            imageUrl: extractImageUrl(equipment.Image),
            logoUrl: equipment.logo,
            order: equipment.order || 0,
            createdAt: equipment.created_at,
            children: sortedProblems.map(problem => {
              // Extraire les photos du problème
              const extractProblemPhotos = (photosData) => {
                if (!photosData) return [];
                try {
                  // Si c'est déjà un tableau
                  if (Array.isArray(photosData)) return photosData;
                  // Si c'est une chaîne JSON
                  if (typeof photosData === 'string') {
                    const parsed = JSON.parse(photosData);
                    return Array.isArray(parsed) ? parsed : [];
                  }
                  return [];
                } catch (e) {
                  console.error('Error parsing problem photos:', e);
                  return [];
                }
              };

              // Trier les services par leur ordre
              const sortedServices = (problem.service || []).sort((a, b) => {
                const orderA = a.order || 0;
                const orderB = b.order || 0;
                return orderA - orderB;
              });

              return {
                id: problem.id,
                name: problem.name || 'Problème sans nom',
                type: 'problem',
                active: problem.actif !== false,
                equipmentId: problem.equipment,
                equipment_id: problem.equipment,
                categoryId: equipment.category,
                category_id: equipment.category,
                description: problem.description,
                photos: extractProblemPhotos(problem.photos), // Ajout des photos
                createdAt: problem.created_at,
                children: sortedServices.map(service => ({
                  id: service.id,
                  name: service.name || 'Service sans nom',
                  type: 'service',
                  active: service.actif !== false,
                  problemId: service.problem,
                  problem_id: service.problem,
                  equipmentId: problem.equipment,
                  equipment_id: problem.equipment,
                  categoryId: equipment.category,
                  category_id: equipment.category,
                  description: service.description,
                  pricingModel: mapPricingModel(service.PricingModel),
                  basePrice: service.basePrice,
                  vatRate: extractVatRate(service.TVA),
                  warrantyMonths: service.Guarantee,
                  estimatedDuration: service.estimatedDuration,
                  skillRequirements: parseSkills(service.requiredSkills),
                  allowFieldAmendments: service.landModifications || false,
                  customerValidationRequired: service.clientValidation || false,
                  produit_fournissable: Boolean(service.produit_fournissable),
                  prix_avec_produit: service.prix_avec_produit,
                  prix_sans_produit: service.prix_sans_produit,
                  description_produit: service.description_produit || '',
                  image_produit: service.image_produit || '',
                  necessite_materiel: service.necessite_materiel !== false,
                  order: service.order || 0,
                  createdAt: service.created_at
                }))
              };
            })
          };
        })
      };
    });
  };

  const uploadFileToBunny = async (file, folder = 'categories') => {
    try {
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `${timestamp}_${sanitizedName}`;
      
      const fileUrl = await bunny.uploadImage(file);
      
      return fileUrl;
    } catch (error) {
      console.error('Error uploading file to Bunny.net:', error);
      throw new Error(`Échec de l'upload: ${error.message}`);
    }
  };

  // Nouvelle fonction pour uploader les photos de problème
  const uploadProblemPhotos = async (photos) => {
    const uploadedPhotos = [];
    
    for (const photo of photos) {
      if (photo.file && photo.file instanceof File) {
        try {
          const fileUrl = await uploadFileToBunny(photo.file, 'problems');
          uploadedPhotos.push({
            url: fileUrl,
            
          });
        } catch (error) {
          console.error('Error uploading problem photo:', error);
          throw new Error(`Erreur lors de l'upload de la photo: ${error.message}`);
        }
      } else if (photo.url) {
        // Photo existante, garder l'URL
        uploadedPhotos.push({
          url: photo.url,
         
        });
      }
    }
    
    return uploadedPhotos;
  };

  const deleteFileFromBunny = async (fileUrl) => {
    if (!fileUrl || !fileUrl.includes('b-cdn.net')) {
      return false;
    }
    
    try {
      const urlParts = fileUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const folder = urlParts[urlParts.length - 2];
      
      if (bunny.deleteImage) {
        await bunny.deleteImage(`${folder}/${fileName}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting file from Bunny.net:', error);
      return false;
    }
  };

  const updateCategoryOrder = async (categoryId, newOrder) => {
    try {
      const { error } = await categoryService.updateCategory(categoryId, { order: newOrder });
      if (error) throw error;
    } catch (error) {
      console.error('Error updating category order:', error);
      throw error;
    }
  };

  const getNextOrder = (items) => {
    if (!items || items.length === 0) return 0;
    const maxOrder = Math.max(...items.map(item => item.order || 0));
    return maxOrder + 1;
  };

  const handleDragStart = (node) => {
    if (node.type !== 'category') {
      alert('Seules les catégories peuvent être réorganisées par glisser-déposer');
      return false;
    }
    setDraggedNode(node);
    return true;
  };

  const handleDrop = async (targetNode) => {
    if (!draggedNode || draggedNode.id === targetNode.id) {
      setDraggedNode(null);
      return;
    }

    if (draggedNode.type !== 'category' || targetNode.type !== 'category') {
      alert('Le déplacement n\'est possible qu\'entre catégories');
      setDraggedNode(null);
      return;
    }

    try {
      setUploading(true);
      
      const currentCategories = [...catalogData];
      const draggedIndex = currentCategories.findIndex(cat => cat.id === draggedNode.id);
      const targetIndex = currentCategories.findIndex(cat => cat.id === targetNode.id);
      
      if (draggedIndex === -1 || targetIndex === -1) return;
      
      const reorderedCategories = [...currentCategories];
      const [removed] = reorderedCategories.splice(draggedIndex, 1);
      reorderedCategories.splice(targetIndex, 0, removed);
      
      const updatePromises = reorderedCategories.map((category, index) => 
        updateCategoryOrder(category.id, index)
      );
      
      await Promise.all(updatePromises);
      await fetchCatalogData();
      
      console.log(`Catégorie "${draggedNode.name}" déplacée avec succès`);
      
    } catch (error) {
      console.error('Error reordering categories:', error);
      alert('Erreur lors du déplacement de la catégorie. Veuillez réessayer.');
    } finally {
      setDraggedNode(null);
      setUploading(false);
    }
  };

  const handleSave = async (updatedNode) => {
    try {
      setUploading(true);
      
      let imageUrl = updatedNode.imageUrl;
      let logoUrl = updatedNode.logoUrl;
      let problemPhotos = updatedNode.problemPhotos || [];

      // Upload des images pour les catégories
      if (updatedNode.type === 'category') {
        if (updatedNode.image instanceof File) {
          try {
            imageUrl = await uploadFileToBunny(updatedNode.image, 'categories/images');
          } catch (uploadError) {
            console.error('Erreur lors de l\'upload de l\'image:', uploadError);
            alert('Erreur lors de l\'upload de l\'image. Le reste des données sera sauvegardé.');
          }
        }

        if (updatedNode.logo instanceof File) {
          try {
            logoUrl = await uploadFileToBunny(updatedNode.logo, 'categories/logos');
          } catch (uploadError) {
            console.error('Erreur lors de l\'upload du logo:', uploadError);
            alert('Erreur lors de l\'upload du logo. Le reste des données sera sauvegardé.');
          }
        }

        updatedNode = {
          ...updatedNode,
          imageUrl: imageUrl || updatedNode.Image,
          logoUrl: logoUrl || updatedNode.logo
        };
      }

      // Upload des photos pour les problèmes
      if (updatedNode.type === 'problem' && problemPhotos.length > 0) {
        try {
          const hasNewPhotos = problemPhotos.some(photo => photo.file instanceof File);
          if (hasNewPhotos) {
            const uploadedPhotos = await uploadProblemPhotos(problemPhotos);
            updatedNode.photos = uploadedPhotos;
          } else {
            updatedNode.photos = problemPhotos;
          }
        } catch (uploadError) {
          console.error('Erreur lors de l\'upload des photos du problème:', uploadError);
          alert('Erreur lors de l\'upload des photos. Le reste des données sera sauvegardé.');
        }
      }

      if (updatedNode.type === 'service') {
        let imageProduit = updatedNode.image_produit;

        if (updatedNode.produitImage instanceof File) {
          try {
            imageProduit = await uploadFileToBunny(updatedNode.produitImage, 'services/products');
          } catch (uploadError) {
            console.error('Erreur lors de l\'upload de l\'image produit:', uploadError);
            alert('Erreur lors de l\'upload de l\'image produit. Le reste des données sera sauvegardé.');
          }
        }

        updatedNode = {
          ...updatedNode,
          image_produit: imageProduit || updatedNode.image_produit || null,
        };
      }

      // Définir l'ordre pour les nouveaux éléments
      if (editMode === 'add' || editMode === 'create-child') {
        let existingItems = [];
        
        if (updatedNode.type === 'category') {
          existingItems = catalogData;
        } else if (updatedNode.type === 'equipment' && parentNode) {
          existingItems = parentNode.children || [];
        } else if (updatedNode.type === 'problem' && parentNode) {
          existingItems = parentNode.children || [];
        } else if (updatedNode.type === 'service' && parentNode) {
          existingItems = parentNode.children || [];
        }
        
        updatedNode.order = getNextOrder(existingItems);
      }

      const supabaseData = prepareDataForSupabase(updatedNode);
      
      if (editMode === 'add') {
        if (updatedNode.type === 'category') {
          await categoryService.addCategory(supabaseData);
        } else if (updatedNode.type === 'equipment') {
          await equipmentService.addEquipment(supabaseData);
        } else if (updatedNode.type === 'problem') {
          await problemService.addProblem(supabaseData);
        } else if (updatedNode.type === 'service') {
          await serviceService.addService(supabaseData);
        }
      } else if (editMode === 'create-child') {
        if (updatedNode.type === 'equipment') {
          await equipmentService.addEquipment(supabaseData);
        } else if (updatedNode.type === 'problem') {
          await problemService.addProblem(supabaseData);
        } else if (updatedNode.type === 'service') {
          await serviceService.addService(supabaseData);
        }
      } else {
        if (updatedNode.type === 'category') {
          await categoryService.updateCategory(updatedNode.id, supabaseData);
        } else if (updatedNode.type === 'equipment') {
          await equipmentService.updateEquipment(updatedNode.id, supabaseData);
        } else if (updatedNode.type === 'problem') {
          await problemService.updateProblem(updatedNode.id, supabaseData);
        } else if (updatedNode.type === 'service') {
          await serviceService.updateService(updatedNode.id, supabaseData);
        }
      }

      await fetchCatalogData();
      setSelectedNode(null);
      setEditMode(null);
      setParentNode(null);
      
    } catch (error) {
      console.error('Error saving node:', error);
      alert(`❌ Erreur lors de la sauvegarde: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const prepareDataForSupabase = (node) => {
    const commonFields = {
      name: node.name,
      description: node.description || null,
      actif: node.active,
      order: node.order || null
    };

    switch (node.type) {
      case 'category':
        return {
          ...commonFields,
          logo: node.logoUrl || null,
          Image: node.imageUrl ? [node.imageUrl] : null
        };
      
      case 'equipment':
        return {
          ...commonFields,
          category: node.categoryId || node.category_id || node.parentId,
          logo: node.logoUrl || null,
          Image: node.imageUrl ? [node.imageUrl] : null
        };
      
      case 'problem':
        return {
          ...commonFields,
          equipment: node.equipmentId || node.equipment_id || node.parentId,
          photos: node.photos ? JSON.stringify(node.photos) : null // Stocker les photos en JSON
        };
      
      case 'service':
        return {
          ...commonFields,
          problem: node.problemId || node.problem_id || node.parentId,
          PricingModel: mapPricingModelToFrench(node.pricingModel),
          basePrice: parseFloat(node.basePrice) || 0,
          TVA: `${node.vatRate}`,
          Guarantee: node.warrantyMonths || 0,
          estimatedDuration: node.estimatedDuration || 60,
          requiredSkills: Array.isArray(node.skillRequirements) 
            ? node.skillRequirements.join(', ')
            : node.skillRequirements || '',
          landModifications: node.allowFieldAmendments || false,
          clientValidation: node.customerValidationRequired || false,
          produit_fournissable: Boolean(node.produit_fournissable),
          prix_avec_produit: node.produit_fournissable
            ? (parseFloat(node.basePrice) || null)
            : null,
          prix_sans_produit: node.produit_fournissable
            ? (parseFloat(node.prix_sans_produit) || null)
            : null,
          description_produit: node.produit_fournissable
            ? (node.description_produit || null)
            : null,
          image_produit: node.produit_fournissable
            ? (node.image_produit || null)
            : null,
          necessite_materiel: node.necessite_materiel !== false,
        };
      
      default:
        return commonFields;
    }
  };

  const mapPricingModel = (pricingModel) => {
    if (!pricingModel) return 'fixed';
    
    const mapping = {
      'prix fixe': 'fixed',
      'sur devis': 'quote',
      'tarification horaire': 'hourly'
    };
    return mapping[pricingModel] || 'fixed';
  };

  const extractVatRate = (tvaString) => {
    if (!tvaString) return '20';
    const match = tvaString.match(/(\d+(\.\d+)?)/);
    return match ? match[1] : '20';
  };

  const parseSkills = (skillsString) => {
    if (!skillsString) return [];
    return skillsString.split(/[,;\n]/)
      .map(skill => skill.trim())
      .filter(skill => skill.length > 0);
  };

  const { categories, equipments, problems } = useMemo(() => {
    const cats = [];
    const equips = [];
    const probs = [];

    const traverse = (items, currentCategory = null, currentEquipment = null) => {
      items?.forEach(item => {
        if (item.type === 'category') {
          cats.push(item);
          if (item.children) traverse(item.children, item, null);
        } else if (item.type === 'equipment') {
          equips.push(item);
          if (item.children) traverse(item.children, currentCategory, item);
        } else if (item.type === 'problem') {
          probs.push(item);
        }
      });
    };

    traverse(catalogData);
    return { categories: cats, equipments: equips, problems: probs };
  }, [catalogData]);

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    setEditMode('edit');
    setParentNode(null);
  };

  const mapPricingModelToFrench = (pricingModel) => {
    const mapping = {
      'fixed': 'prix fixe',
      'quote': 'sur devis',
      'hourly': 'tarification horaire'
    };
    return mapping[pricingModel] || 'prix fixe';
  };

  const handleCancel = () => {
    setSelectedNode(null);
    setEditMode(null);
    setParentNode(null);
  };

  const handleEdit = (node, mode) => {
    setSelectedNode(node);
    setEditMode(mode || 'edit');
    setParentNode(null);
  };

  const handleDelete = async (node) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer "${node?.name}" ?\n\nCette action est irréversible.`)) {
      try {
        if (node.type === 'category') {
          if (node.imageUrl && node.imageUrl.includes('b-cdn.net')) {
            await deleteFileFromBunny(node.imageUrl);
          }
          if (node.logoUrl && node.logoUrl.includes('b-cdn.net')) {
            await deleteFileFromBunny(node.logoUrl);
          }
        }
        
        // Supprimer les photos des problèmes
        if (node.type === 'problem' && node.photos && node.photos.length > 0) {
          for (const photo of node.photos) {
            if (photo.url && photo.url.includes('b-cdn.net')) {
              await deleteFileFromBunny(photo.url);
            }
          }
        }
        
        if (node.type === 'category') {
          await categoryService.deleteCategory(node.id);
        } else if (node.type === 'equipment') {
          await equipmentService.deleteEquipment(node.id);
        } else if (node.type === 'problem') {
          await problemService.deleteProblem(node.id);
        } else if (node.type === 'service') {
          await serviceService.deleteService(node.id);
        }
        
        alert(`"${node?.name}" a été supprimé du catalogue.`);
        await fetchCatalogData();
        
      } catch (error) {
        console.error('Error deleting node:', error);
        alert('Erreur lors de la suppression. Veuillez réessayer.');
      }
    }
  };

  const handleAddChild = (parent, mode) => {
    if (parent.type === 'root') {
      setSelectedNode(null);
      setEditMode('add');
      setParentNode(null);
    } else {
      setSelectedNode(null);
      setEditMode(mode || 'create-child');
      setParentNode(parent);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };

  const handleImport = (result) => {
    alert(`Import réussi!\n\n${result?.recordsImported} enregistrements importés depuis ${result?.fileName}`);
  };

  const handleBulkEdit = (operation) => {
    alert(`Opération en masse terminée!\n\nType: ${operation?.operation}\n${operation?.affectedServices} services modifiés`);
  };

  const countTotalNodes = (nodes) => {
    let count = 0;
    const traverse = (items) => {
      items?.forEach(item => {
        count++;
        if (item?.children) {
          traverse(item?.children);
        }
      });
    };
    traverse(nodes);
    return count;
  };

  const countActiveServices = (nodes) => {
    let count = 0;
    const traverse = (items) => {
      items?.forEach(item => {
        if (item.type === 'service' && item.active) {
          count++;
        }
        if (item.children) {
          traverse(item.children);
        }
      });
    };
    traverse(nodes);
    return count;
  };

  const countServicesByPricingModel = (nodes, pricingModel) => {
    let count = 0;
    const traverse = (items) => {
      items?.forEach(item => {
        if (item.type === 'service' && item.pricingModel === pricingModel) {
          count++;
        }
        if (item.children) {
          traverse(item.children);
        }
      });
    };
    traverse(nodes);
    return count;
  };

  const renderUploadingOverlay = () => {
    if (!uploading) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <div className="text-center">
              <p className="font-medium text-lg mb-1">Upload en cours...</p>
              <p className="text-sm text-muted-foreground">
                Veuillez patienter pendant le téléchargement des images
              </p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div className="bg-primary h-2.5 rounded-full animate-pulse w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <QuickActionToolbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement du catalogue...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <QuickActionToolbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={fetchCatalogData} iconName="RefreshCw">
                Réessayer
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {renderUploadingOverlay()}
      
      <RoleBasedHeader />
      <QuickActionToolbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="flex gap-4 border-b border-border">
            <button
              onClick={() => setActiveTab('catalog')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'catalog' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Catalogue de services
            </button>
            <button
              onClick={() => setActiveTab('zones')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'zones' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Zones d'intervention
            </button>
          </div>
        </div>

        {activeTab === 'zones' ? (
          <ServiceZoneManager />
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                  Gestionnaire de catalogue
                </h1>
                <p className="text-muted-foreground">
                  Configuration des services, tarifs et hiérarchie du catalogue
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  onClick={fetchCatalogData}
                  disabled={loading}
                >
                  Actualiser
                </Button>
                <Button
                  iconName="Plus"
                  onClick={() => handleAddChild({ id: 'root', type: 'root' })}
                  disabled={uploading}
                >
                  Nouvel élément
                </Button>
              </div>
            </div>

            <SearchFilterBar
              onSearch={handleSearch}
              onFilter={handleFilter}
              totalItems={countTotalNodes(catalogData)}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-4 xl:col-span-4">
                <div className="bg-card rounded-lg border border-border overflow-hidden">
                  <div className="p-4 border-b border-border bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="FolderTree" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-foreground">
                          Arborescence
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          {catalogData.length} catégories
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 max-h-[calc(100vh-320px)] overflow-y-auto scrollbar-thin">
                    {catalogData.length === 0 ? (
                      <div className="text-center py-8">
                        <Icon name="FolderOpen" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Aucune catégorie trouvée</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => handleAddChild({ id: 'root', type: 'root' })}
                          disabled={uploading}
                        >
                          Créer la première catégorie
                        </Button>
                      </div>
                    ) : (
                      <div>
                        {catalogData.map((node) => (
                          <CategoryTreeNode
                            key={node.id}
                            node={node}
                            level={0}
                            onSelect={handleNodeSelect}
                            selectedId={selectedNode?.id}
                            onDragStart={handleDragStart}
                            onDrop={handleDrop}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onAddChild={handleAddChild}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {showBulkPanel && (
                  <div className="mt-6 animate-slide-up">
                    <BulkOperationsPanel
                      onImport={handleImport}
                      onExport={() => console.log('Export')}
                      onBulkEdit={handleBulkEdit}
                    />
                  </div>
                )}
              </div>

              <div className="lg:col-span-8 xl:col-span-8">
                <div className="bg-card rounded-lg border border-border h-[calc(100vh-280px)] overflow-hidden">
                  <ServiceEditPanel
                    mode={editMode}
                    selectedNode={selectedNode}
                    parentNode={parentNode}
                    categories={categories}
                    equipments={equipments}
                    problems={problems}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    uploading={uploading}
                  />
                </div>

                {showValidationPanel && (
                  <div className="mt-6 animate-slide-up">
                    <ValidationTestPanel catalogData={catalogData} />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Icon name="Layers" size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">{catalogData.length}</div>
                    <div className="text-xs text-muted-foreground">Catégories</div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} className="text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      {countActiveServices(catalogData)}
                    </div>
                    <div className="text-xs text-muted-foreground">Services actifs</div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Icon name="DollarSign" size={20} className="text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      {countServicesByPricingModel(catalogData, 'fixed')}
                    </div>
                    <div className="text-xs text-muted-foreground">Prix fixes</div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      {countServicesByPricingModel(catalogData, 'quote')}
                    </div>
                    <div className="text-xs text-muted-foreground">Sur devis</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceCatalogManager;